import { useAppSelector } from '@/app/hooks'
import { css } from '@emotion/react'
import React, { useMemo } from 'react'

export type TabItem<Key extends string> = {
  key: Key
  label: string
}

type Props<Key extends string> = {
  items: TabItem<Key>[]
  active: Key
  onChange: (key: Key) => void
}

export const Tabs = <Key extends string>({ items, active, onChange }: Props<Key>) => {
  const design = useAppSelector(state => state.design)

  const tabStyle = useMemo(
    () =>
      css({
        display: 'flex',
        width: '100%',
      }),
    [],
  )

  const itemStyle = (isActive: boolean) =>
    css({
      flex: 1,
      height: '46px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: 'bold',
      letterSpacing: '0.7px',
      cursor: 'pointer',
      border: 'none',
      background: isActive ? design?.background.cardColor : design?.background.backgroundColor,
      color: isActive ? design?.theme.brandColor : design?.form.borderColor,
    })

  return (
    <div css={tabStyle} role="tablist">
      {items.map(item => (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={item.key === active}
          css={itemStyle(item.key === active)}
          onClick={() => onChange(item.key)}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}
